import { cloneElement, FC, useRef, useState } from 'react'
import { useFloating, flip, shift, autoUpdate, offset, arrow } from '@floating-ui/react-dom'
import Foco from 'react-foco'
import { CSSTransition } from 'react-transition-group'
import { FloatingPortal } from '@floating-ui/react-dom-interactions'
import cx from 'classnames'
import Styles from './WhiteTooltip.module.scss'
import { TooltipProps, StaticSidesConfig, Side } from './Types'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { IconButton } from '../IconButton/IconButton'
import { ReactComponent as CloseIcon } from 'assets/icons/close16.svg'

const transitionClassNames = {
  enter: Styles['tooltip-enter'],
  enterActive: Styles['tooltip-enter-active'],
  exit: Styles['tooltip-exit'],
  exitActive: Styles['tooltip-exit-active'],
}

const RIGHT_START_ARROW_TOP = 13
const MAIN_AXIS_OFFSET = 16
const CROSS_AXIS_OFFSET = -16

export const WhiteTooltip: FC<React.PropsWithChildren<TooltipProps & { callOut?: boolean }>> = ({
  children,
  label,
  mouseEnterDelay = 0,
  placement = 'top',
  className,
  strategy = 'absolute',
  shiftPadding = 0,
  usePortal,
  callOut = false,
}) => {
  const arrowRef = useRef(null)
  const {
    x,
    y,
    reference,
    floating,
    placement: realPlacement,
    refs,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
  } = useFloating({
    placement,
    strategy,
    whileElementsMounted: (reference, floating, update) => {
      return autoUpdate(reference, floating, update, {
        animationFrame: true,
      })
    },
    middleware: [
      offset({ mainAxis: MAIN_AXIS_OFFSET, crossAxis: CROSS_AXIS_OFFSET }),
      flip(),
      shift({ padding: shiftPadding }),
      arrow({ element: arrowRef }),
    ],
  })
  const [isTooltipVisible, setIsTooltipVisible] = useState(callOut)
  const timeout = useRef<NodeJS.Timeout>()

  const showTooltip = () => {
    if (timeout.current || callOut) return

    timeout.current = setTimeout(() => setIsTooltipVisible(true), mouseEnterDelay)
  }

  const hideTooltip = () => {
    if (callOut) return

    clearTimeout(timeout.current)
    timeout.current = undefined
    setIsTooltipVisible(false)
  }

  const handleFocusOutside = (e: MouseEvent | TouchEvent | FocusEvent) => {
    const tooltipChild = refs.reference.current as HTMLElement | null
    if (!tooltipChild?.contains?.(e.target as Node)) {
      hideTooltip()
    }
  }

  const [placementSide] = realPlacement.split('-')

  const staticSide = StaticSidesConfig[placementSide as Side]

  const arrowTop = () => {
    switch (realPlacement) {
      case 'right-start':
        return RIGHT_START_ARROW_TOP
      default:
        return arrowY
    }
  }

  return (
    <>
      {cloneElement(children, {
        ref: reference,
        onMouseEnter: showTooltip,
        onFocus: showTooltip,
        onMouseLeave: hideTooltip,
        onBlur: hideTooltip,
      })}
      <ConditionalWrapper
        condition={usePortal}
        wrapper={(children) => <FloatingPortal id="floating-ui-root">{children}</FloatingPortal>}
      >
        <CSSTransition in={isTooltipVisible} unmountOnExit classNames={transitionClassNames} timeout={150}>
          <div
            className={cx(Styles.tooltip, { [Styles.visible]: isTooltipVisible, [Styles.callOut]: callOut }, className)}
            ref={floating}
            role="tooltip"
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
            }}
          >
            <Foco onClickOutside={handleFocusOutside} onFocusOutside={handleFocusOutside}>
              <div
                className={cx(Styles.arrow, Styles[placementSide])}
                style={{
                  left: arrowX != null ? `${arrowX}px` : '',
                  top: arrowY != null ? `${arrowTop()}px` : '',
                  right: '',
                  bottom: '',
                  [staticSide]: ['left', 'right'].includes(staticSide) ? '-24px' : '-9px',
                }}
                ref={arrowRef}
              >
                <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 26">
                  <path
                    d="m30.36 5.26-2.54-4.23a2 2 0 0 0-3.48.09l-1.82 3.4A16 16 0 0 1 8.4 13H8a8 8 0 0 0-8 8v3c0 1.1.9 2 2 2h48a2 2 0 0 0 2-2v-3.03c0-4.4-3.57-7.97-7.97-7.97-5.6 0-10.8-2.94-13.67-7.74Z"
                    fill="#fff"
                  />
                </svg>
              </div>
              <div className={Styles.labelContainer}>
                {label}
                {callOut && (
                  <IconButton
                    variant="tertiary"
                    className={Styles.closeButton}
                    onPress={() => setIsTooltipVisible(false)}
                    icon={CloseIcon}
                  />
                )}
              </div>
            </Foco>
          </div>
        </CSSTransition>
      </ConditionalWrapper>
    </>
  )
}
